import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, Search, Sparkles } from "lucide-react";
import Footer from "@/components/landing/Footer";
import ConnectWalletModal from "@/components/ConnectWalletModal";
import { trackMetaPixelIssueCardClick } from "@/lib/metaPixel";

const faqCategories = [
  {
    id: "getting-started",
    name: "Getting Started",
    questions: [
      {
        q: "How do I apply for a card?",
        a: `Applying for your Trust Wallet Card is simple and takes less than 2 minutes:\n\n1. Click the "Issue Card" button on our homepage\n2. Connect your Trust Wallet wallet securely\n3. Complete the identity verification (KYC) process\n4. Choose your card tier (Standard or Metal)\n5. Confirm your shipping address\n\nNo credit check is required — your card limit is based on your crypto holdings in your connected wallet.`,
      },
      {
        q: "How long does it take to get my card?",
        a: `Once your application is approved, your physical card will be shipped within 3-5 business days.\n\nIn the meantime, you can immediately add your virtual card to Apple Pay or Google Pay and start making purchases right away!\n\nExpress shipping options are available in select regions for faster delivery.`,
      },
      {
        q: "What documents do I need to apply?",
        a: `To complete the KYC process, you'll need:\n\n• A valid government-issued photo ID (passport, driver's license, or national ID card)\n• A selfie for facial verification\n• Proof of address (utility bill, bank statement, or government letter — issued within the last 3 months)\n\nThe entire verification process is handled securely through our app and typically takes under 5 minutes.`,
      },
      {
        q: "Which countries are supported?",
        a: `Trust Wallet Card is currently available in 150+ countries and territories worldwide, including:\n\n• United States & Canada\n• European Union & United Kingdom\n• Australia & New Zealand\n• Most of Asia-Pacific\n• Select countries in Latin America, Africa, and the Middle East\n\nWe're continuously expanding our coverage. Check the app for the most up-to-date list of supported countries.`,
      },
    ],
  },
  {
    id: "card-features",
    name: "Card Features & Limits",
    questions: [
      {
        q: "What are the card spending limits?",
        a: `Card spending limits depend on your verification tier and card type:\n\n**Standard Card:**\n• Daily spending: up to $10,000\n• Monthly spending: up to $50,000\n• ATM withdrawal: up to $2,000/day\n\n**Metal Card:**\n• Daily spending: up to $50,000\n• Monthly spending: up to $250,000\n• ATM withdrawal: up to $5,000/day\n\nLimits may vary based on your jurisdiction and verification level.`,
      },
      {
        q: "Where can I use my card?",
        a: `Your Trust Wallet Card is accepted at over 80 million merchants worldwide — anywhere Visa and Mastercard are accepted. This includes:\n\n• Online stores and subscriptions\n• Retail stores and restaurants\n• ATMs for cash withdrawals\n• Contactless payments via Apple Pay and Google Pay\n• International purchases in 150+ currencies`,
      },
      {
        q: "What cryptocurrencies can I spend?",
        a: `You can spend a wide range of cryptocurrencies directly from your Trust Wallet, including:\n\n• Tether (USDT) — TRC-20, ERC-20, BEP-20\n• Tron (TRX)\n• Ethereum (ETH)\n• BNB (Binance Coin)\n• Bitcoin (BTC)\n• USD Coin (USDC)\n• Polygon (MATIC)\n• Solana (SOL)\n• And 50+ more tokens\n\nCrypto-to-fiat conversion happens automatically at the point of sale at real-time market rates.`,
      },
      {
        q: "Are there any fees?",
        a: `We believe in transparent pricing with no hidden fees:\n\n• **Card issuance:** Free (Standard), Free with $100K+ balance (Metal)\n• **Monthly maintenance:** $0\n• **Domestic transactions:** 0% fee\n• **International transactions:** 0% conversion fee\n• **ATM withdrawals:** First 5 per month free, then $1.50 each\n• **Crypto conversion:** 0.5% spread at point of sale\n\nNo annual fees. No surprise charges. Ever.`,
      },
    ],
  },
  {
    id: "digital-payments",
    name: "Digital Payments",
    questions: [
      {
        q: "Does the card support Apple Pay?",
        a: `Yes! Your Trust Wallet Card fully supports Apple Pay. Simply:\n\n1. Open the Wallet app on your iPhone\n2. Tap the "+" button to add a new card\n3. Select "Debit or Credit Card"\n4. Scan your card or enter details manually\n5. Complete verification\n\nOnce added, you can use tap-to-pay with your iPhone, Apple Watch, iPad, or Mac for contactless payments in stores, in apps, and on the web.`,
      },
      {
        q: "Does the card support Google Pay?",
        a: `Absolutely! Google Pay is fully supported. To set it up:\n\n1. Open Google Pay on your Android device\n2. Tap "Add a payment method"\n3. Enter your Trust Wallet Card details\n4. Complete the verification process\n\nYou can then make contactless payments with your Android phone or Wear OS watch at any NFC-enabled terminal worldwide.`,
      },
      {
        q: "Can I use the card for online purchases?",
        a: `Yes, your Trust Wallet Card works seamlessly for online purchases. You can use it for:\n\n• E-commerce shopping (Amazon, eBay, etc.)\n• Subscription services (Netflix, Spotify, etc.)\n• Digital goods and services\n• In-app purchases\n• Online bill payments\n\nFor extra security, all online transactions are protected with 3D Secure (3DS) authentication.`,
      },
    ],
  },
  {
    id: "security",
    name: "Security & Technology",
    questions: [
      {
        q: "How does the card connect to my non-custodial wallet?",
        a: `Your Trust Wallet Card connects to your wallet through a secure, read-only smart contract integration:\n\n• We never access your private keys or seed phrase\n• The connection uses industry-standard WalletConnect protocol\n• Transactions are authorized in real-time through your wallet\n• You maintain full custody of your assets at all times\n\nWhen you make a purchase, the exact amount is converted from crypto to fiat through our regulated liquidity partners and settled instantly with the merchant.`,
      },
      {
        q: "Is my wallet safe?",
        a: `Your wallet security is our top priority. Here's how we protect you:\n\n• **Non-custodial:** We never hold your private keys\n• **Read-only access:** We can only view balances, not move funds without your approval\n• **Smart contract audited:** Our contracts are audited by CertiK\n• **Multi-signature security:** Critical operations require multiple approvals\n• **Biometric authentication:** Every transaction requires your fingerprint or Face ID\n\nYour funds remain in your wallet until the moment of purchase.`,
      },
      {
        q: "What happens if my card is lost or stolen?",
        a: `If your card is lost or stolen, you can take immediate action:\n\n1. **Freeze your card instantly** through the app with a single tap\n2. **Report it** to our 24/7 support team\n3. **Receive a replacement** — a new virtual card is issued immediately, and a physical replacement ships within 2-3 business days\n\nSince your funds are stored in your wallet (not on the card), a lost card does not put your crypto at risk. The card can only be used with your biometric authentication.`,
      },
      {
        q: "How is transaction data protected?",
        a: `We employ bank-grade security measures to protect your data:\n\n• **256-bit AES encryption** for all data in transit and at rest\n• **SOC 2 Type II** certified infrastructure\n• **PCI DSS Level 1** compliance — the highest security standard in payments\n• **GDPR compliant** data handling practices\n• **Real-time fraud monitoring** with AI-powered detection\n• **Zero-knowledge architecture** — we minimize the data we store\n\nRegular penetration testing and security audits ensure continuous protection.`,
      },
    ],
  },
  {
    id: "metal-card",
    name: "Premium Metal Card",
    questions: [
      {
        q: "Does the Metal Card cost extra?",
        a: `The Metal Card is completely free for eligible holders. To qualify, you need to maintain a balance of $100,000 or more in USDT (or equivalent) in your connected Trust Wallet.\n\nThere are no additional monthly fees, annual charges, or hidden costs associated with the Metal Card. It's our way of rewarding our most valued users.`,
      },
      {
        q: "What are the Metal Card exclusive benefits?",
        a: `Metal Card holders enjoy premium benefits including:\n\n• **2x Rewards Multiplier** — earn double crypto rewards on every purchase\n• **Airport Lounge Access** — complimentary access to 1,300+ airport lounges worldwide via LoungeKey\n• **Priority Support 24/7** — dedicated support line with response times under 60 seconds\n• **Higher Limits** — up to $50,000/day spending and $5,000/day ATM withdrawals\n• **Concierge Service** — personal assistance for travel, dining, and entertainment bookings\n• **No Foreign Transaction Fees** — 0% on all international purchases`,
      },
      {
        q: "What is the Metal Card made of?",
        a: `The Metal Card is crafted from premium stainless steel with a weight of 18 grams — over 3x heavier than a standard plastic card. Key specifications:\n\n• **Material:** 304-grade stainless steel with PVD coating\n• **Finish:** Brushed metal with laser-etched details\n• **Chip:** EMV-certified contact and contactless NFC chip\n• **Design:** Minimalist matte black with the Trust logo\n\nThe card is designed to be both a functional payment tool and a statement piece.`,
      },
      {
        q: "Can I upgrade from Standard to Metal?",
        a: `Yes! You can upgrade from Standard to Metal at any time once you meet the eligibility requirement of holding $100,000+ in USDT (or equivalent) in your connected wallet.\n\nTo upgrade:\n1. Open the Trust Wallet Card app\n2. Go to Card Settings\n3. Select "Upgrade to Metal"\n4. Confirm your shipping address\n\nYour new Metal Card will arrive within 5-7 business days. Your existing card number and virtual card will remain active until you activate the Metal Card.`,
      },
    ],
  },
  {
    id: "transactions",
    name: "Transactions & Support",
    questions: [
      {
        q: "Where can I see my transactions?",
        a: `You can view all your transactions in multiple ways:\n\n• **In the app:** Real-time transaction feed with detailed breakdowns of crypto spent, exchange rates, and merchant information\n• **Push notifications:** Instant alerts for every transaction\n• **Monthly statements:** Downloadable PDF statements\n• **Export options:** CSV export for tax reporting and accounting\n\nAll transaction history is available from day one and never expires.`,
      },
      {
        q: "What if a transaction fails?",
        a: `If a transaction fails, here's what happens:\n\n• **No charge:** Failed transactions are never charged to your wallet\n• **Instant notification:** You'll receive an alert explaining why the transaction failed\n• **Common reasons:** Insufficient balance, merchant restrictions, security flags, or network issues\n\nIf you believe a transaction failed incorrectly, contact our support team. Most issues are resolved within minutes.`,
      },
      {
        q: "How do refunds work?",
        a: `Refunds are processed automatically:\n\n• **Merchant refunds:** When a merchant issues a refund, it's credited back to your wallet in the original cryptocurrency within 5-10 business days\n• **Dispute refunds:** If you dispute a charge, we investigate and process eligible refunds within 30 days\n• **Exchange rate:** Refunds are processed at the original exchange rate — you won't lose money due to price fluctuations\n\nYou can track refund status in real-time through the app.`,
      },
      {
        q: "How do I contact support?",
        a: `Our support team is available 24/7 through multiple channels:\n\n• **In-app chat:** Fastest response — typically under 2 minutes\n• **Help Center:** Comprehensive guides and tutorials at our Help Center page\n• **Social Media:** @TrustWalletCard on Twitter/X\n\nMetal Card holders receive priority support with dedicated agents and response times under 60 seconds.`,
      },
    ],
  },
];

const FAQPage = () => {
  const [activeCategory, setActiveCategory] = useState("getting-started");
  const [openQuestions, setOpenQuestions] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [walletOpen, setWalletOpen] = useState(false);

  const toggleQuestion = (question: string) => {
    setOpenQuestions((prev) => {
      const next = new Set(prev);
      if (next.has(question)) {
        next.delete(question);
      } else {
        next.add(question);
      }
      return next;
    });
  };

  const filteredCategories = searchQuery.trim()
    ? faqCategories
        .map((cat) => ({
          ...cat,
          questions: cat.questions.filter(
            (q) =>
              q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
              q.a.toLowerCase().includes(searchQuery.toLowerCase())
          ),
        }))
        .filter((cat) => cat.questions.length > 0)
    : faqCategories.filter((cat) => cat.id === activeCategory);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border/40 bg-background/90 backdrop-blur-xl">
        <div className="container flex h-20 items-center justify-between">
          <Link to="/" className="flex items-center gap-3" aria-label="Trust homepage">
            <img src="/trust-assets/wallets/trust-logo-name.png" alt="Trust logo" className="h-9 w-auto" />
          </Link>
          <button
            onClick={() => { trackMetaPixelIssueCardClick("faq-cta"); setWalletOpen(true); }}
            className="inline-flex items-center gap-2 rounded-2xl bg-foreground px-6 py-3 text-sm font-semibold text-background transition-transform hover:-translate-y-0.5"
          >
            Get Started
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="relative border-b border-border/50 py-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,hsl(var(--primary)/0.07)_0,transparent_58%)]" />
          <div className="container relative text-center">
            <h1 className="text-4xl font-semibold tracking-[-0.04em] text-foreground sm:text-5xl lg:text-6xl">
              Frequently Asked Questions
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-muted-foreground">
              Everything you need to know about your Trust Wallet Card. Can&apos;t find what you&apos;re looking for? Contact our support team.
            </p>

            {/* Search */}
            <div className="mx-auto mt-10 max-w-lg">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search questions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full rounded-2xl border border-border/60 bg-card/80 py-4 pl-12 pr-6 text-base text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/40 focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Category tabs */}
        {!searchQuery.trim() && (
          <section className="border-b border-border/50 bg-card/30">
            <div className="container py-4">
              <div className="flex flex-wrap items-center justify-center gap-2">
                {faqCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
                      activeCategory === cat.id
                        ? "bg-primary text-primary-foreground"
                        : "bg-card border border-border/60 text-muted-foreground hover:text-foreground hover:bg-secondary"
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQ content */}
        <section className="py-16">
          <div className="container">
            <div className="mx-auto max-w-3xl space-y-12">
              {filteredCategories.map((category) => (
                <div key={category.id}>
                  <h2 className="mb-6 text-2xl font-semibold text-foreground">{category.name}</h2>
                  <div className="space-y-3">
                    {category.questions.map((item) => (
                      <div
                        key={item.q}
                        className="rounded-2xl border border-border/60 bg-card/80 overflow-hidden"
                      >
                        <button
                          onClick={() => toggleQuestion(item.q)}
                          className="flex w-full items-center justify-between px-6 py-5 text-left"
                        >
                          <span className="text-base font-semibold text-foreground pr-4">{item.q}</span>
                          <ChevronDown
                            className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200 ${
                              openQuestions.has(item.q) ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                        {openQuestions.has(item.q) && (
                          <div className="border-t border-border/40 px-6 pb-5 pt-4">
                            <div className="text-base leading-relaxed text-muted-foreground whitespace-pre-line">
                              {item.a}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="border-t border-border/50 bg-card/50 py-20">
          <div className="container text-center">
            <h2 className="text-3xl font-semibold text-foreground">Still have questions?</h2>
            <p className="mx-auto mt-4 max-w-xl text-lg text-muted-foreground">
              Our support team is here to help 24/7. Get in touch and we&apos;ll get back to you as soon as possible.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <button
                onClick={() => { trackMetaPixelIssueCardClick("faq-cta"); setWalletOpen(true); }}
                className="inline-flex items-center gap-2 rounded-2xl bg-primary px-8 py-4 text-lg font-semibold text-primary-foreground shadow-[var(--shadow-button)] transition-transform hover:-translate-y-0.5"
              >
                Issue Card
                <ArrowRight className="h-5 w-5" />
              </button>
              <Link
                to="/contact"
                className="inline-flex items-center rounded-2xl border border-border bg-card px-8 py-4 text-lg font-semibold text-foreground transition-colors hover:bg-secondary"
              >
                Contact Support
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ConnectWalletModal open={walletOpen} onOpenChange={setWalletOpen} />
    </div>
  );
};

export default FAQPage;
